'use client';

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { formatEventCount } from '@/lib/format/eventCount';

export interface DiscoverDaySectionDateLabel {
  /** Day-of-month number rendered in the themed `--accent` (e.g. `20`). */
  readonly dayNumber: number;
  /**
   * Day name shown next to the number. Parent composes the "Tonight · Wednesday"
   * prefix when the section's date matches today in the city's timezone. Per
   * DS 1.5.0 (`disc-day__label`) this carries only the prefix + weekday — the
   * calendar date lives in `monthDayLabel` / the sub-line.
   */
  readonly dayLabel: string;
  /**
   * Uppercase month + day for the `disc-day__sub` line (e.g. `"APR 29"`). Leads
   * the sub-line as `"{monthDayLabel} · {count} EVENTS"`. The parent
   * (`buildDayLabel`) emits this already-uppercased so jsdom assertions see the
   * literal text (CSS `text-transform` does not alter text nodes).
   */
  readonly monthDayLabel: string;
  /**
   * Optional weather/atmosphere hint appended after the event count
   * (e.g. `"☀ 78°F"`). Empty string or `undefined` hides the hint. (C)-scope —
   * passed through verbatim, never recased here.
   */
  readonly subLabel?: string;
  /** Number of events bucketed under this day. */
  readonly count: number;
}

export type DiscoverTimelineKind = 'in-progress' | 'still-ahead';

interface DiscoverDaySectionDayProps {
  /** Day-grouped header. Default variant. */
  variant?: 'day';
  /** Header view-model — see `DiscoverDaySectionDateLabel`. */
  dateLabel: DiscoverDaySectionDateLabel;
  /**
   * WEB-1075 — the bucket's city-local calendar date, `YYYY-MM-DD`
   * (`bucket.localDate` from `groupEventsByDay`). Keys the section as
   * `id="discover-day-{YYYY-MM-DD}"` + `data-local-date` so the day strip can
   * hash-jump to it. A `dv-day-{dd}` key would collide once a range wraps to
   * the same day number a month later. `data-local-date` is a contract: the
   * day strip finds loaded days through `[data-local-date]`. Omitted or
   * malformed → no id, no attribute (never a colliding fallback).
   */
  localDate?: string;
  /** Event rows for this day. Typically a list of `<DiscoverEventCard />`. */
  children: React.ReactNode;
  /** Additional classes merged onto the outer `<section>`. */
  className?: string;
}

interface DiscoverDaySectionTimelineProps {
  /**
   * WEB-671 / DPL-20 — single-day timeline group head. Replaces the calendar
   * day number + weekday with In-progress / Still-ahead copy. Multi-day
   * presets keep the `'day'` variant.
   */
  variant: 'timeline';
  timelineKind: DiscoverTimelineKind;
  title: string;
  detail: string;
  children: React.ReactNode;
  className?: string;
}

export type DiscoverDaySectionProps = DiscoverDaySectionDayProps | DiscoverDaySectionTimelineProps;

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** WEB-1075 — the hash-jump target id for a city-local `YYYY-MM-DD` day. */
export function discoverDaySectionId(localDate: string): string {
  return `discover-day-${localDate}`;
}

/**
 * DiscoverDaySection — day-grouped header + cards for the `/discover` list view.
 *
 * Pure presentational. The parent (M3.3) derives day buckets and supplies the
 * already-resolved `dateLabel` (including any "Tonight · " prefix on
 * `dayLabel`). Children are rendered vertically with mock-fidelity spacing.
 */
function TimelineGroupHead({
  timelineKind,
  title,
  detail,
  children,
  className,
}: Omit<DiscoverDaySectionTimelineProps, 'variant'>): React.ReactElement {
  const headingId = `disc-tl-${timelineKind}`;
  return (
    <section
      className={cn('disc-tl flex w-full flex-col', className)}
      data-testid="discover-timeline-group"
      data-timeline-kind={timelineKind}
      aria-labelledby={headingId}
    >
      <header className="disc-tl__head" data-testid="discover-timeline-group-head">
        <h2 id={headingId} className="disc-tl__title" data-testid="discover-timeline-group-title">
          {title}
        </h2>
        <p className="disc-tl__detail" data-testid="discover-timeline-group-detail">
          {detail}
        </p>
      </header>
      <div className="disc-tl__body flex flex-col" data-testid="discover-timeline-group-body">
        {children}
      </div>
    </section>
  );
}

export function DiscoverDaySection(props: DiscoverDaySectionProps): React.ReactElement {
  // Called before the variant branch so the hook order never changes. Only the
  // `'day'` fallback (no valid `localDate`) uses it.
  const fallbackId = React.useId();
  if (props.variant === 'timeline') {
    return (
      <TimelineGroupHead
        timelineKind={props.timelineKind}
        title={props.title}
        detail={props.detail}
        className={props.className}
      >
        {props.children}
      </TimelineGroupHead>
    );
  }

  const { dateLabel, localDate, children, className } = props;
  const { dayNumber, dayLabel, monthDayLabel, subLabel, count } = dateLabel;
  const trimmedSubLabel = subLabel?.trim();
  // DS 1.5.0 `disc-day__sub` shape: "{MONTH} {day} · {count} EVENTS[ · weather]".
  // `monthDayLabel` ("APR 29") leads; the optional (C)-scope weather hint stays
  // appended last, passed through verbatim. `monthDayLabel` is empty only when
  // the parent failed to parse the local date — fall back to a clean count so
  // no dangling " · " separator leaks through.
  const countLabel = formatEventCount(count, { casing: 'sentence' });
  const dateAndCount = monthDayLabel ? `${monthDayLabel} · ${countLabel}` : countLabel;
  const subLine = [dateAndCount, trimmedSubLabel].filter(Boolean).join(' · ');

  const keyedDate = localDate && LOCAL_DATE_PATTERN.test(localDate) ? localDate : undefined;
  const sectionId = keyedDate ? discoverDaySectionId(keyedDate) : undefined;

  // Single shared heading id for the section/heading aria linkage. With a
  // valid `localDate` it derives from the unique, date-keyed section id.
  // Without one, the display labels are not unique (two sections can share
  // weekday + day number + month), so the fallback is a stable per-instance
  // `useId`, sanitized to an id-safe token.
  const headingId = sectionId
    ? `${sectionId}-title`
    : `disc-day-${fallbackId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  // WEB-1075 — slim one-line head: numeral, h2 and sub-line share one
  // baseline. Geometry (34/28px numeral, 16/15px h2, padding, scroll-margin)
  // lives on the named classes in `styles/components/discover-day-head.css`;
  // only colour + family utilities stay here.
  return (
    <section
      id={sectionId}
      data-local-date={keyedDate}
      className={cn('disc-day flex w-full flex-col', className)}
      data-testid="discover-day-section"
      aria-labelledby={headingId}
    >
      <header
        className="disc-day__head border-b border-loci-border"
        data-testid="discover-day-section-head"
      >
        <span
          className="disc-day__number text-ds-accent font-display"
          data-testid="discover-day-section-day-number"
          aria-hidden="true"
        >
          {dayNumber}
        </span>
        <div className="disc-day__labels">
          <h2
            id={headingId}
            className="disc-day__label text-loci-text font-display"
            data-testid="discover-day-section-day-label"
          >
            {dayLabel}
          </h2>
          <p
            className="disc-day__sub text-loci-text-muted font-mono"
            data-testid="discover-day-section-sub-label"
          >
            {subLine}
          </p>
        </div>
      </header>
      <div className="disc-day__body flex flex-col" data-testid="discover-day-section-body">
        {children}
      </div>
    </section>
  );
}
